import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Icon,
  Banner,
  Spinner,
} from '@shopify/polaris'
import { CircleTickMinor } from '@shopify/polaris-icons'
import toast from 'react-hot-toast'
import { billingApi } from '../services/api'

interface BillingPlan {
  id: string
  name: string
  price: number
  trialDays: number
  features: {
    maxConversations: number
    maxAgentSeats: number
    automation: string
    campaigns?: boolean
    analytics?: string
    support: string
  }
}

const PLAN_DETAILS = {
  STARTER: {
    id: 'STARTER',
    name: 'Starter',
    description: 'Perfect for small businesses getting started',
    highlights: [
      '1,000 conversations/month',
      '2 agent seats',
      'Basic automation',
      'Email support',
      '14-day free trial',
    ],
  },
  PROFESSIONAL: {
    id: 'PROFESSIONAL',
    name: 'Professional',
    description: 'Best for growing businesses',
    highlights: [
      '5,000 conversations/month',
      '5 agent seats',
      'Advanced automation',
      'Campaign management',
      'Priority email support',
      '14-day free trial',
    ],
    popular: true,
  },
  BUSINESS: {
    id: 'BUSINESS',
    name: 'Business',
    description: 'Enterprise-grade features',
    highlights: [
      '20,000 conversations/month',
      '15 agent seats',
      'Enterprise automation',
      'Advanced campaigns',
      'Advanced analytics',
      'Phone & chat support',
      '14-day free trial',
    ],
  },
}

export default function BillingPlanSelectPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [plans, setPlans] = useState<BillingPlan[]>([])
  const [loading, setLoading] = useState(true)
  const [subscribing, setSubscribing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const storeId = searchParams.get('store')
  const shop = searchParams.get('shop')
  const errorParam = searchParams.get('error')

  useEffect(() => {
    if (errorParam === 'declined') {
      setError('Subscription was declined. Please select a plan to continue using the app.')
    }
  }, [errorParam])

  useEffect(() => {
    loadPlans()
  }, [])

  const loadPlans = async () => {
    try {
      setLoading(true)
      const response = await billingApi.getPlans()
      const plansData = Object.entries(response.data.data.plans).map(([key, value]: [string, any]) => ({
        id: key,
        name: value.name,
        price: value.price,
        trialDays: value.trialDays,
        features: value.features,
      }))
      setPlans(plansData)
    } catch (err: any) {
      console.error('Failed to load plans:', err)
      toast.error('Failed to load subscription plans')
      setError('Failed to load plans. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPlan = async (planId: string) => {
    if (!storeId) {
      toast.error('Store ID is missing')
      return
    }

    try {
      setSubscribing(planId)
      const response = await billingApi.createCharge(storeId, planId)
      const { confirmationUrl } = response.data.data

      // Redirect to Shopify for approval
      window.location.href = confirmationUrl
    } catch (err: any) {
      console.error('Failed to create subscription:', err)
      toast.error('Failed to create subscription. Please try again.')
      setSubscribing(null)
    }
  }

  if (loading) {
    return (
      <Page title="Select Your Plan">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    )
  }

  return (
    <Page
      title="Choose Your Plan"
      subtitle="Start your 14-day free trial - no credit card required during trial"
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="warning" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <BlockStack gap="600">
            <InlineStack gap="400" align="center" blockAlign="stretch">
              {plans.map((plan) => {
                const details = PLAN_DETAILS[plan.id as keyof typeof PLAN_DETAILS]
                const isPopular = details?.popular
                const isSubscribing = subscribing === plan.id

                return (
                  <div
                    key={plan.id}
                    style={{
                      flex: 1,
                      position: 'relative',
                    }}
                  >
                    {isPopular && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-12px',
                          right: '16px',
                          zIndex: 1,
                        }}
                      >
                        <Badge tone="success">Most Popular</Badge>
                      </div>
                    )}

                    <Card>
                      <BlockStack gap="400">
                        {/* Plan Header */}
                        <BlockStack gap="200">
                          <Text as="h2" variant="headingLg">
                            {details?.name || plan.name}
                          </Text>
                          <Text as="p" variant="bodyMd" tone="subdued">
                            {details?.description}
                          </Text>
                        </BlockStack>

                        {/* Pricing */}
                        <BlockStack gap="100">
                          <InlineStack gap="100" align="start" blockAlign="baseline">
                            <Text as="p" variant="heading2xl" fontWeight="bold">
                              ${plan.price}
                            </Text>
                            <Text as="p" variant="bodyLg" tone="subdued">
                              /month
                            </Text>
                          </InlineStack>
                          {plan.trialDays > 0 && (
                            <Text as="p" variant="bodySm" tone="success">
                              {plan.trialDays}-day free trial
                            </Text>
                          )}
                        </BlockStack>

                        {/* Features */}
                        <BlockStack gap="300">
                          {details?.highlights.map((highlight, index) => (
                            <InlineStack key={index} gap="200" align="start">
                              <div style={{ marginTop: '2px' }}>
                                <Icon source={CircleTickMinor} tone="success" />
                              </div>
                              <Text as="p" variant="bodyMd">
                                {highlight}
                              </Text>
                            </InlineStack>
                          ))}
                        </BlockStack>

                        {/* CTA Button */}
                        <Button
                          variant={isPopular ? 'primary' : 'secondary'}
                          size="large"
                          fullWidth
                          onClick={() => handleSelectPlan(plan.id)}
                          loading={isSubscribing}
                          disabled={subscribing !== null && !isSubscribing}
                        >
                          {isSubscribing ? 'Processing...' : 'Start Free Trial'}
                        </Button>
                      </BlockStack>
                    </Card>
                  </div>
                )
              })}
            </InlineStack>
          </BlockStack>
        </Layout.Section>

        {/* Additional Info */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">
                What happens next?
              </Text>
              <BlockStack gap="200">
                <InlineStack gap="200" align="start">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    1.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Start your 14-day free trial with full access to all features
                  </Text>
                </InlineStack>
                <InlineStack gap="200" align="start">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    2.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    No credit card required during trial period
                  </Text>
                </InlineStack>
                <InlineStack gap="200" align="start">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    3.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    After trial, your subscription will be charged through your Shopify account
                  </Text>
                </InlineStack>
                <InlineStack gap="200" align="start">
                  <Text as="p" variant="bodyMd" fontWeight="bold">
                    4.
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Cancel anytime - no questions asked
                  </Text>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  )
}
